/**
 * Phone-side rules for the machine runs (F6.9, F6.1, F6.10, F6.2, F6.3, F6.5, F6.7, F6-B1.b).
 * Pure functions of the projected site pack, so every rule is unit-tested. The server repeats
 * every check when a command arrives and turns a gap into a problem on the run, never a refusal.
 */
import { istDateKey, istYYMMDD } from '@/lib/ist'
import type { Grams } from '@/lib/weight'
import type { LastRun, SiteRoll, SitePackSettings, SiteUnit, Step, YieldBand } from '@/offline/sitePack'
import {
  isRunLive,
  liveOutputs,
  type BagView,
  type MachineView,
  type ProcessState,
  type RunView,
} from '../projection'
import type { BalanceFigures } from './balance'

export type Refusal =
  | 'TAG_UNKNOWN'
  | 'TAG_WRONG_KIND'
  | 'TAG_VOID'
  | 'TAG_USED'
  | 'NOT_YOUR_ROLL'
  | 'NOT_A_MACHINE_LABEL'
  | 'MACHINE_HAS_LABEL'
  | 'MACHINE_NO_LABEL'
  | 'OTHER_MACHINE_LABEL'
  | 'MACHINE_BUSY'
  | 'MACHINE_OTHER_SITE'
  | 'NO_SETTINGS'
  | 'LOT_ON_HOLD'
  | 'LOT_USED_UP'
  | 'LOT_ELSEWHERE'
  | 'OTHER_FARMER'
  | 'OTHER_CROP'
  | 'OTHER_STREAM'
  | 'NOT_ON_TASK'
  | 'NOT_ISSUED'
  | 'ISSUED_ELSEWHERE'
  | 'DUPLICATE'
  | 'NOT_THIS_BATCH'
  | 'NOT_A_SACK'

export type Verdict<T> = { ok: true; value: T } | { ok: false; reason: Refusal; detail?: string }

const norm = (c: string) => c.trim().toUpperCase()

// ---------------------------------------------------------------------------------------------
// Codes
// ---------------------------------------------------------------------------------------------

/** The roll a code belongs to: prefix + digits within the roll's range. */
export function rollOf(rolls: readonly SiteRoll[], code: string): SiteRoll | null {
  const c = norm(code)
  for (const r of rolls) {
    const prefix = r.payload_prefix.toUpperCase()
    if (!c.startsWith(prefix)) continue
    const rest = c.slice(prefix.length)
    if (!/^\d+$/.test(rest)) continue
    const n = Number(rest)
    if (n >= r.start_sequence && n <= r.end_sequence) return r
  }
  return null
}

export type CodeKind =
  | { kind: 'DEAD'; status: 'VOID' | 'RETIRED' }
  | { kind: 'BAG'; bag: BagView }
  | { kind: 'MACHINE'; machine: MachineView }
  | { kind: 'ROLL'; roll: SiteRoll }
  | { kind: 'UNKNOWN' }

/** Look a scanned (or typed) code up in the phone's data. What it shows comes from data. */
export function classifyCode(state: ProcessState, code: string): CodeKind {
  const c = norm(code)
  const dead = state.deadTags.get(c)
  if (dead) return { kind: 'DEAD', status: dead }
  const bag = state.bagByPayload.get(c)
  if (bag) return bag.status === 'VOID' ? { kind: 'DEAD', status: 'VOID' } : { kind: 'BAG', bag }
  const machine = state.machineByLabel.get(c)
  if (machine) return { kind: 'MACHINE', machine }
  const roll = rollOf(state.pack.rolls, c)
  if (roll) return { kind: 'ROLL', roll }
  return { kind: 'UNKNOWN' }
}

/**
 * A fresh code from one of the site's rolls of `kind`, issued to this person, never used:
 * output sacks (OUTPUT_BAG), QC seals (QC_SEAL), machine labels (MACHINE).
 */
export function checkFreshCode(
  state: ProcessState,
  code: string,
  kind: 'OUTPUT_BAG' | 'QC_SEAL' | 'MACHINE',
  actorId: string,
  alsoUsed: ReadonlySet<string> = new Set(),
): Verdict<string> {
  const c = norm(code)
  const k = classifyCode(state, c)
  if (k.kind === 'DEAD') return { ok: false, reason: k.status === 'VOID' ? 'TAG_VOID' : 'TAG_USED' }
  if (k.kind === 'BAG' || k.kind === 'MACHINE')
    return { ok: false, reason: kind === 'MACHINE' ? 'NOT_A_MACHINE_LABEL' : 'TAG_WRONG_KIND' }
  if (k.kind === 'UNKNOWN') return { ok: false, reason: kind === 'MACHINE' ? 'NOT_A_MACHINE_LABEL' : 'TAG_UNKNOWN' }
  if (k.roll.kind !== kind)
    return { ok: false, reason: kind === 'MACHINE' ? 'NOT_A_MACHINE_LABEL' : 'TAG_WRONG_KIND' }
  if (k.roll.issued_to_id && k.roll.issued_to_id !== actorId) return { ok: false, reason: 'NOT_YOUR_ROLL' }
  if (state.usedTags.has(c) || alsoUsed.has(c)) return { ok: false, reason: 'TAG_USED' }
  return { ok: true, value: c }
}

// ---------------------------------------------------------------------------------------------
// F6.1 Open run
// ---------------------------------------------------------------------------------------------

export function liveRunOn(state: ProcessState, equipmentId: string): RunView | null {
  for (const r of state.runs.values())
    if (r.equipment_id === equipmentId && isRunLive(r.status)) return r
  return null
}

/** Machine label at open: a bound MACHINE label of this site, no run open on it. */
export function checkMachineScan(state: ProcessState, code: string): Verdict<MachineView> {
  const k = classifyCode(state, code)
  if (k.kind !== 'MACHINE') return { ok: false, reason: 'NOT_A_MACHINE_LABEL' }
  const busy = liveRunOn(state, k.machine.id)
  if (busy) return { ok: false, reason: 'MACHINE_BUSY', detail: busy.code }
  return { ok: true, value: k.machine }
}

/**
 * Machine label at open, after the machine was picked from the list (pilot): only the bound
 * MACHINE label of that same machine names it. Refusals: the machine has no label yet
 * (MACHINE_NO_LABEL, label it first), not a machine label, or the label of another machine
 * (OTHER_MACHINE_LABEL, detail = that machine's name · code). Offline: the site list only.
 * The value is the label as the server knows it (upper-cased).
 */
export function checkMachineLabelFor(
  state: ProcessState,
  code: string,
  machine: Pick<MachineView, 'id' | 'label_payload'>,
): Verdict<string> {
  if (!machine.label_payload) return { ok: false, reason: 'MACHINE_NO_LABEL' }
  const k = classifyCode(state, code)
  if (k.kind !== 'MACHINE') return { ok: false, reason: 'NOT_A_MACHINE_LABEL' }
  if (k.machine.id !== machine.id)
    return {
      ok: false,
      reason: 'OTHER_MACHINE_LABEL',
      detail: [k.machine.name, k.machine.code].filter(Boolean).join(' · '),
    }
  return { ok: true, value: norm(code) }
}

/**
 * The settings row a new run uses: newest version in force today, the machine's own before
 * crop-wide, variety-specific first.
 */
export function bandFor(
  bands: readonly YieldBand[],
  args: { equipment_id: string; step_code: string; crop_id: string; crop_variety_id?: string | null },
  now: number = Date.now(),
): YieldBand | null {
  const today = istDateKey(now)
  const fits = bands.filter(
    (b) =>
      b.step_code === args.step_code &&
      b.crop_id === args.crop_id &&
      b.effective_from.slice(0, 10) <= today,
  )
  const pick = (list: YieldBand[]) =>
    list.sort((a, b) => b.version - a.version || b.effective_from.localeCompare(a.effective_from))[0] ?? null
  // The machine's own rows first, then crop-wide rows (no machine); variety-specific before plain.
  for (const forMachine of [
    fits.filter((b) => b.equipment_id === args.equipment_id),
    fits.filter((b) => !b.equipment_id),
  ]) {
    const variety = args.crop_variety_id
      ? pick(forMachine.filter((b) => b.crop_variety_id === args.crop_variety_id))
      : null
    const band = variety ?? pick(forMachine.filter((b) => !b.crop_variety_id))
    if (band) return band
  }
  return null
}

/** A bag scanned at open names its lot: committed, not held, its bags sealed or stored here. */
export function checkOpenLotBag(
  state: ProcessState,
  code: string,
  origin: SiteUnit | null,
  already: readonly string[],
): Verdict<SiteUnit> {
  const k = classifyCode(state, code)
  if (k.kind === 'DEAD') return { ok: false, reason: k.status === 'VOID' ? 'TAG_VOID' : 'TAG_USED' }
  if (k.kind !== 'BAG') return { ok: false, reason: k.kind === 'UNKNOWN' ? 'TAG_UNKNOWN' : 'TAG_WRONG_KIND' }
  const unit = state.units.get(k.bag.unit_id)
  if (!unit) return { ok: false, reason: 'TAG_UNKNOWN' }
  if (already.includes(unit.id)) return { ok: false, reason: 'DUPLICATE' }
  if (unit.hold !== 'NONE' || k.bag.hold !== 'NONE') return { ok: false, reason: 'LOT_ON_HOLD' }
  if (!['SEALED', 'STORED', 'ISSUED'].includes(k.bag.status)) return { ok: false, reason: 'LOT_USED_UP' }
  if (k.bag.location_id === null && k.bag.status !== 'ISSUED') return { ok: false, reason: 'LOT_ELSEWHERE' }
  if (origin) {
    if (unit.stream !== origin.stream) return { ok: false, reason: 'OTHER_STREAM' }
    if (unit.crop_id !== origin.crop_id) return { ok: false, reason: 'OTHER_CROP' }
    if (
      origin.stream === 'MAIN' &&
      (unit.farmer_id !== origin.farmer_id || unit.season_code !== origin.season_code)
    )
      return { ok: false, reason: 'OTHER_FARMER' }
  }
  return { ok: true, value: unit }
}

/** Kilos planned: the latest net weights of the scanned lots' live bags at the site. */
export function plannedGrams(state: ProcessState, unitIds: readonly string[]): Grams {
  let g = 0
  for (const b of state.bags.values())
    if (unitIds.includes(b.unit_id) && ['SEALED', 'STORED', 'ISSUED'].includes(b.status))
      g += b.net_g_current ?? b.net_g_gate ?? 0
  return g
}

/**
 * Flush (F6.1): skipped only when the phone is online, the machine is ours alone and the run
 * before was brand with the same farmer, crop and season and closed. A CONV run never flushes.
 */
export function flushNeeded(args: {
  stream: 'MAIN' | 'CONV'
  online: boolean
  shared_use: boolean
  last: Pick<LastRun, 'stream' | 'farmer_id' | 'crop_id' | 'season_code' | 'status'> | null
  origin: Pick<SiteUnit, 'farmer_id' | 'crop_id' | 'season_code'>
}): boolean {
  if (args.stream === 'CONV') return false
  if (!args.online || args.shared_use || !args.last) return true
  const l = args.last
  const sameOrigin =
    l.stream === 'MAIN' &&
    l.farmer_id === args.origin.farmer_id &&
    l.crop_id === args.origin.crop_id &&
    l.season_code === args.origin.season_code
  return !(sameOrigin && (l.status === 'CLOSED' || l.status === 'BALANCED'))
}

/** Allergen clean (R30): needed when the crop changed, or the last crop is unknown. */
export function allergenCleanNeeded(args: {
  online: boolean
  shared_use: boolean
  last: Pick<LastRun, 'crop_id'> | null
  crop_id: string
}): boolean {
  if (!args.online || args.shared_use || !args.last?.crop_id) return true
  return args.last.crop_id !== args.crop_id
}

/** PR-yymmdd-<machine code>-<n>; the server may renumber n. */
export function runCode(machineCode: string, n: number, at: number = Date.now()): string {
  // The server takes at most 24 characters. A longer machine code still gets a code that fits:
  // the server then ignores the proposal's shape and gives the run its own number.
  return `PR-${istYYMMDD(at)}-${machineCode.toUpperCase()}-${n}`.slice(0, RUN_CODE_MAX)
}

/** uc-core's process_runs.code length. */
const RUN_CODE_MAX = 24

// ---------------------------------------------------------------------------------------------
// F6.10 Issue
// ---------------------------------------------------------------------------------------------

export interface IssueTaskLike {
  run_id: string
  unit_ids: readonly string[]
  issued: ReadonlyMap<string, number | null>
}

export function checkIssueBag(state: ProcessState, task: IssueTaskLike, code: string): Verdict<BagView> {
  const k = classifyCode(state, code)
  if (k.kind === 'DEAD') return { ok: false, reason: k.status === 'VOID' ? 'TAG_VOID' : 'TAG_USED' }
  if (k.kind !== 'BAG') return { ok: false, reason: k.kind === 'UNKNOWN' ? 'TAG_UNKNOWN' : 'TAG_WRONG_KIND' }
  const bag = k.bag
  if (task.issued.has(bag.container_id)) return { ok: false, reason: 'DUPLICATE' }
  if (!task.unit_ids.includes(bag.unit_id)) {
    const unit = state.units.get(bag.unit_id)
    const runOrigin = state.runs.get(task.run_id)
    if (unit && runOrigin && runOrigin.stream === 'MAIN' && unit.farmer_id !== runOrigin.farmer_id)
      return { ok: false, reason: 'OTHER_FARMER' }
    return { ok: false, reason: 'NOT_ON_TASK' }
  }
  if (bag.hold !== 'NONE' || state.units.get(bag.unit_id)?.hold !== 'NONE')
    return { ok: false, reason: 'LOT_ON_HOLD' }
  if (bag.status === 'ISSUED') return { ok: false, reason: 'ISSUED_ELSEWHERE' }
  if (!['SEALED', 'STORED'].includes(bag.status)) return { ok: false, reason: 'LOT_USED_UP' }
  if (!bag.location_id) return { ok: false, reason: 'LOT_ELSEWHERE' }
  return { ok: true, value: bag }
}

/** A reading outside the usual bag range is typed twice (measurements rule). */
export function needsSecondEntry(readG: Grams, s: Pick<SitePackSettings, 'weight_min_g' | 'weight_max_g'>): boolean {
  return readG < s.weight_min_g || readG > s.weight_max_g
}

// ---------------------------------------------------------------------------------------------
// F6.3 Weigh in
// ---------------------------------------------------------------------------------------------

export interface WeighInMatch {
  bag: BagView
  /** The phone saw the bag's issue to this run: its issue weight is the input weight. */
  issueSeen: boolean
}

/** Has this tag gone into any run's hopper (a run input)? */
function tippedIn(state: ProcessState, code: string): boolean {
  for (const r of state.runs.values()) if (r.inputs.some((i) => norm(i.qr_payload) === code)) return true
  return false
}

export function checkWeighInBag(
  state: ProcessState,
  run: RunView,
  code: string,
): Verdict<WeighInMatch> {
  const c = norm(code)
  const k = classifyCode(state, c)
  if (k.kind === 'DEAD') return { ok: false, reason: k.status === 'VOID' ? 'TAG_VOID' : 'TAG_USED' }
  if (k.kind !== 'BAG') return { ok: false, reason: k.kind === 'UNKNOWN' ? 'TAG_UNKNOWN' : 'TAG_WRONG_KIND' }
  const bag = k.bag
  if (run.inputs.some((i) => i.container_id === bag.container_id || norm(i.qr_payload) === c))
    return { ok: false, reason: 'DUPLICATE' }
  // Already tipped into a run. (Not `usedTags`: every output sack's tag is "used" as a fresh code,
  // yet that sack is what the next step tips in.)
  if (bag.status === 'CONSUMED' || tippedIn(state, c)) return { ok: false, reason: 'TAG_USED' }
  const unit = state.units.get(bag.unit_id)
  if (!unit) return { ok: false, reason: 'TAG_UNKNOWN' }
  // Origin first: the reason shown is the mismatch, whatever the issue says.
  if (unit.crop_id !== run.crop_id) return { ok: false, reason: 'OTHER_CROP' }
  if (run.stream === 'MAIN') {
    if (unit.stream !== 'MAIN') return { ok: false, reason: 'OTHER_STREAM' }
    if (unit.farmer_id !== run.farmer_id || unit.season_code !== run.season_code)
      return { ok: false, reason: 'OTHER_FARMER' }
    if (unit.hold !== 'NONE' || bag.hold !== 'NONE') return { ok: false, reason: 'LOT_ON_HOLD' }
  }
  if (bag.issued_run_id === run.id) return { ok: true, value: { bag, issueSeen: true } }
  if (bag.issued_run_id && bag.issued_run_id !== run.id) return { ok: false, reason: 'NOT_ISSUED' }
  // Not seen: accepted only when it is of the run's origin and here at this site.
  if (['SEALED', 'STORED'].includes(bag.status) && bag.location_id)
    return { ok: true, value: { bag, issueSeen: false } }
  return { ok: false, reason: 'NOT_ISSUED' }
}

// ---------------------------------------------------------------------------------------------
// F6.2 / F6.5 Outputs and close
// ---------------------------------------------------------------------------------------------

export function flushProgress(run: RunView): { flush_g: Grams; min_g: Grams; short_g: Grams; met: boolean } {
  const flush_g = liveOutputs(run)
    .filter((o) => o.stream_key === 'FLUSH')
    .reduce((a, o) => a + o.net_g, 0)
  const min_g = run.flush_required ? (run.flush_min_g ?? 0) : 0
  const short_g = Math.max(0, min_g - flush_g)
  return { flush_g, min_g, short_g, met: !run.flush_required || short_g === 0 }
}

/**
 * Bags in whose weight this phone does not know (null, never 0): the balance cannot be shown
 * from them — the server's figures come with the next list download.
 */
export function unknownInputWeights(run: Pick<RunView, 'inputs'>): number {
  return run.inputs.filter((i) => i.weight_g === null).length
}

/** The run's figures for the balance, from its inputs and live output sacks. */
export function runFigures(run: RunView, step: Step | undefined): BalanceFigures {
  const input_g = run.inputs.reduce((a, i) => a + (i.weight_g ?? 0), 0)
  let flush_g = 0
  let product_g = 0
  let byproduct_g = 0
  let reject_g = 0
  for (const o of liveOutputs(run)) {
    if (o.stream_key === 'FLUSH') {
      flush_g += o.net_g
      continue
    }
    const def = step?.outputs.find((x) => x.key === o.stream_key)
    if (!def || def.unit_type === 'REJECT') reject_g += o.net_g
    else if (def.unit_type === 'BYPRODUCT') byproduct_g += o.net_g
    else if (def.product || def.unit_type === 'BATCH') product_g += o.net_g
  }
  return { input_g, flush_g, product_g, byproduct_g, reject_g }
}

export type CloseGap =
  | { kind: 'NO_CLEANOUT' }
  | { kind: 'NO_INPUT' }
  | { kind: 'FLUSH_SHORT'; short_g: Grams }
  | { kind: 'OUTPUT_MISSING'; key: string }
  | { kind: 'BAGS_NOT_IN'; count: number }
  | { kind: 'NOT_STOPPED' }

/** What still stops Close run (the button stays grey with this list). */
export function closeGaps(run: RunView, step: Step | undefined, stopped: boolean): CloseGap[] {
  const gaps: CloseGap[] = []
  if (!run.cleanout_confirmed_at) gaps.push({ kind: 'NO_CLEANOUT' })
  if (run.inputs.length === 0) gaps.push({ kind: 'NO_INPUT' })
  const f = flushProgress(run)
  if (!f.met) gaps.push({ kind: 'FLUSH_SHORT', short_g: f.short_g })
  const live = liveOutputs(run)
  for (const o of step?.outputs ?? [])
    if (!live.some((x) => x.stream_key === o.key) && !run.zero_streams.includes(o.key))
      gaps.push({ kind: 'OUTPUT_MISSING', key: o.key })
  const notIn = issuedNotIn(run)
  if (notIn.length) gaps.push({ kind: 'BAGS_NOT_IN', count: notIn.length })
  if (!stopped) gaps.push({ kind: 'NOT_STOPPED' })
  return gaps
}

/** Bags issued to the run and not scanned in. */
export function issuedNotIn(run: RunView): string[] {
  const inIds = new Set(run.inputs.map((i) => i.container_id))
  return [...run.issued.keys()].filter((cid) => !inIds.has(cid))
}

/** Cancel works only before any bag went in (F6.1). */
export function canCancel(run: RunView): boolean {
  return isRunLive(run.status) && run.inputs.length === 0
}

/** Red ordinary tie for flush, by-product, waste and any ordinary run (F6.5). */
export function tieFor(stream: 'MAIN' | 'CONV'): 'CONV' | 'NONE' {
  return stream === 'CONV' ? 'CONV' : 'NONE'
}

// ---------------------------------------------------------------------------------------------
// F6.7 QC sample
// ---------------------------------------------------------------------------------------------

/** Is this run's step the last before packing on the crop's route? */
export function isLastBeforePacking(state: ProcessState, run: Pick<RunView, 'crop_id' | 'step_code'>): boolean {
  const route = state.pack.routes.find((r) => r.crop_id === run.crop_id)
  if (!route || route.steps.length === 0) return true
  return route.steps[route.steps.length - 1] === run.step_code
}

/** A batch in one of these states on the server has nothing left to sample. */
const GONE_UNIT_STATUSES: ReadonlySet<string> = new Set(['CONSUMED', 'VOID', 'DESTROYED'])

export interface SampleCandidate {
  unit_id: string
  output_key: string
  sacks: string[]
  net_g: Grams
  run: RunView | null
}

/**
 * Brand batches of a closed run that go to packing and have no sample yet (F6.7); with
 * `confirming`, held batches the quality person asked a fresh sample of (F7-B1.b).
 */
export function sampleCandidates(state: ProcessState, opts: { confirming?: boolean } = {}): SampleCandidate[] {
  if (opts.confirming) {
    // Held batches the quality person asked a fresh sample of: their sacks are wherever they wait.
    return [...state.units.values()]
      .filter((u) => u.unit_type === 'BATCH' && u.confirm_sample_requested)
      .filter((u) => !(state.samplesByUnit.get(u.id) ?? []).some((s) => s.purpose === 'CONFIRMING'))
      .map((u) => {
        const sacks = [...state.bags.values()].filter(
          (b) => b.unit_id === u.id && !['CONSUMED', 'VOID'].includes(b.status),
        )
        return {
          unit_id: u.id,
          output_key: u.stream_key ?? '',
          sacks: sacks.map((b) => b.qr_payload),
          net_g: u.net_g ?? sacks.reduce((a, b) => a + (b.net_g_current ?? 0), 0),
          run: u.run_id ? (state.runs.get(u.run_id) ?? null) : null,
        }
      })
  }
  const out: SampleCandidate[] = []
  for (const run of state.runs.values()) {
    if (isRunLive(run.status) || run.status === 'CANCELLED') continue
    if (!isLastBeforePacking(state, run)) continue
    const step = state.steps.get(run.step_code)
    const byUnit = new Map<string, SampleCandidate>()
    for (const o of liveOutputs(run)) {
      if (o.stream_key === 'FLUSH' || o.stream !== 'MAIN') continue
      const def = step?.outputs.find((x) => x.key === o.stream_key)
      if (!def || def.unit_type !== 'BATCH' || !def.to_packing) continue
      if ((state.samplesByUnit.get(o.unit_id) ?? []).some((s) => s.purpose !== 'CONFIRMING')) continue
      // Sampled on another phone, or already packed / gone (the server's word in the site list).
      if (o.sampled === true || (o.unit_status && GONE_UNIT_STATUSES.has(o.unit_status))) continue
      const c = byUnit.get(o.unit_id) ?? { unit_id: o.unit_id, output_key: o.stream_key, sacks: [], net_g: 0, run }
      c.sacks.push(o.qr_payload)
      c.net_g += o.net_g
      byUnit.set(o.unit_id, c)
    }
    out.push(...byUnit.values())
  }
  return out
}
